import React, { useState, useEffect, useCallback } from "react";
import { userService } from "../services/userService";
import { FiActivity } from "react-icons/fi";
import KYCHistoryModal from "./KYCHistoryModal";
import { API_BASE_URL, ADMIN_API_BASE_URL } from "../config";
import { toast } from "react-toastify";
import { getImageUrl } from "../utils/docS3";
import { motion, AnimatePresence } from "framer-motion";
import MainHeader from "../components/MainHeader";

// App user interface matching backend data
interface AppUser {
  id: string;
  full_name: string;
  email: string;
  mobile?: string;
  date_of_birth?: string;
  gender?: string;
  father_name?: string;
  kyc_status?: "verified" | "pending" | "rejected" | "not_submitted";
  wallet_balance?: number;
  deposit_balance?: number;
  winnings_balance?: number;
  withdrawable_balance?: number;
  account_status: "active" | "inactive" | "pending";
  last_login?: string;
  created_at: string;
  profile_picture?: string;
  total_challenges_joined?: number;
  total_challenges_won?: number;
  kyc_documents?: {
    id: string;
    type: string;
    status: string;
    submitted_at: string;
    url?: string;
  }[];
  auth_provider?: string;
  email_verified?: boolean;
  mobile_verified?: boolean;
  preferences?: {
    experience_level?: string;
    trading_frequency?: string;
    trading_playgrounds?: string[];
    risk_appetite?: string;
    [key: string]: any;
  };
}

interface UserLog {
  id: string;
  user_id: string;
  user_email?: string;
  user_mobile?: string;
  endpoint: string;
  method: string;
  request_data: any;
  response_status: number;
  response_data: any;
  ip_address?: string;
  user_agent?: string;
  timestamp: string;
  execution_time_ms?: number;
  success: boolean;
  error_message?: string;
}

interface UserLogsData {
  user: {
    id: string;
    full_name: string;
    email: string;
    mobile_number?: string;
  };
  logs: any;
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
  summary: {
    total_requests: number;
    successful_requests: number;
    failed_requests: number;
    avg_execution_time: number;
    success_rate: number;
    last_activity?: string;
  };
}

const statusOptions = ["All", "active", "inactive", "pending"];
const kycOptions = ["All", "verified", "pending", "rejected", "not_submitted"];
const providerOption = ["All", "email", "phone", "google", "facebook", "apple"];

interface AppUserManagementProps {
  onBackToDashboard: () => void;
}

export default function AppUserManagement({
  onBackToDashboard,
}: AppUserManagementProps) {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [kycFilter, setKycFilter] = useState("All");
  const [provider, setProvider] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [resetPasswordData, setResetPasswordData] = useState({
    userId: "",
    newPassword: "",
  });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState({
    start: "",
    end: "",
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState<AppUser | null>(null);
  const [editForm, setEditForm] = useState<{
    full_name: string;
    email: string;
    mobile: string;
    kyc_status: "verified" | "pending" | "rejected" | "not_submitted";
    account_status: "active" | "inactive" | "pending";
    date_of_birth: string;
    gender: string;
    father_name: string;
  }>({
    full_name: "",
    email: "",
    mobile: "",
    kyc_status: "not_submitted",
    account_status: "active",
    date_of_birth: "",
    gender: "",
    father_name: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [userLogs, setUserLogs] = useState<UserLogsData | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(20);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [showKYCHistoryModal, setShowKYCHistoryModal] = useState(false);
  const [showTransactionHistoryModal, setShowTransactionHistoryModal] =
    useState(false);
  const [userTransactions, setUserTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsPerPage, setTransactionsPerPage] = useState(20);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsTotalPages, setTransactionsTotalPages] = useState(1);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userStats, setUserStats] = useState({
    total_users: "",
    active_users: "",
    suspended_users: "",
  });
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        throw new Error("Authentication required");
      }
      // Use userService.getAppUsers to include all filters
      const data = await userService.getAppUsers(
        currentPage,
        perPage,
        search,
        statusFilter !== "All" ? statusFilter : undefined,
        provider !== "All" ? provider : undefined,
        kycFilter !== "All" ? kycFilter : undefined,
        dateRangeFilter.start || undefined,
        dateRangeFilter.end || undefined
      );
      setUsers(data.users);
      setUserStats(data?.stats);
      setTotalUsers(data.total);
      setTotalPages(data.total_pages);
    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    perPage,
    search,
    statusFilter,
    kycFilter,
    dateRangeFilter,
    provider,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleKycChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setKycFilter(e.target.value);
    setCurrentPage(1);
  };
  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProvider(e.target.value);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (field: "start" | "end", value: string) => {
    setDateRangeFilter((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setKycFilter("All");
    setDateRangeFilter({ start: "", end: "" });
    setCurrentPage(1);
  };

  const handleViewProfile = async (user: AppUser) => {
    try {
      // Fetch detailed user profile
      const detailedUser = await userService.getAppUserById(user.id);
      setSelectedUser(detailedUser);
      setShowProfileModal(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch user details"
      );
    }
  };

  const handleToggleStatus = async (userId: string) => {
    try {
      const user = users.find((u) => u.id === userId);
      if (!user) return;

      const newStatus =
        user.account_status === "active" ? "inactive" : "active";
      await userService.updateAppUser(userId, { status: newStatus });

      // Refresh the user list
      fetchUsers();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update user status"
      );
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordData.newPassword.trim()) {
      setError("Please enter a new password");
      return;
    }

    setResetPasswordLoading(true);
    try {
      await userService.resetAppUserPassword(
        resetPasswordData.userId,
        resetPasswordData.newPassword
      );
      setShowResetPasswordModal(false);
      setResetPasswordData({ userId: "", newPassword: "" });
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedUser(null);
  };

  const openResetPasswordModal = (userId: string) => {
    setResetPasswordData({ userId, newPassword: "" });
    setShowResetPasswordModal(true);
  };

  const getKycStatusColor = (status: string) => {
    switch (status) {
      case "verified":
        return "text-green-600 bg-green-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "rejected":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getAccountStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "text-green-600 bg-green-100";
      case "inactive":
        return "text-red-600 bg-red-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const formatCurrency = (amount: number = 0) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const openEditModal = (user: AppUser) => {
    setEditUser(user);
    setEditForm({
      full_name: user.full_name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      kyc_status: (user.kyc_status || "not_submitted") as
        | "verified"
        | "pending"
        | "rejected"
        | "not_submitted",
      account_status: (user.account_status || "active") as
        | "active"
        | "inactive"
        | "pending",
      date_of_birth: user.date_of_birth || "",
      gender: user.gender || "",
      father_name: user.father_name || "",
    });
    setEditError("");
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditUser(null);
    setEditError("");
  };

  const handleEditFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]:
        name === "kyc_status"
          ? (value as "verified" | "pending" | "rejected" | "not_submitted")
          : name === "account_status"
          ? (value as "active" | "inactive" | "pending")
          : value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setEditLoading(true);
    setEditError("");
    try {
      await userService.updateAppUser(editUser.id, editForm);
      setShowEditModal(false);
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      setEditError(
        err instanceof Error ? err.message : "Failed to update user"
      );
    } finally {
      setEditLoading(false);
    }
  };

  // const fetchUserLogs = async (userId: string, page: number = 1, perPage: number = 20) => {
  //   try {
  //     setLogsLoading(true);
  //     const token = localStorage.getItem('adminToken');
  //     const response = await fetch(`${ADMIN_API_BASE_URL}/app-users/${userId}/logs?page=${page}&per_page=${perPage}`, {
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });

  //     if (!response.ok) {
  //       throw new Error('Failed to fetch user logs');
  //     }

  //     const data = await response.json();
  //     setUserLogs(data);
  //   } catch (error) {
  //     console.error('Error fetching user logs:', error);
  //     setError('Failed to fetch user logs');
  //   } finally {
  //     setLogsLoading(false);
  //   }
  // };
  const fetchUserLogs = async (
    userId: string,
    page: number = 1,
    perPage: number = 20
  ) => {
    try {
      setLogsLoading(true);
      const response = await userService.fetchUserLogs(userId, page, perPage);
      setUserLogs(response);
    } catch (err) {
      console.error("Failed to fetch user logs:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to fetch user logs"
      );
      setError(
        err instanceof Error ? err.message : "Failed to fetch user logs"
      );
    } finally {
      setLogsLoading(false);
    }
  };

  const handleViewLogs = async (user: AppUser) => {
    setSelectedUser(user);
    setLogsDialogOpen(true);
    setLogsPage(1);
    await fetchUserLogs(user.id, 1, logsPerPage);
  };

  const handleLogsPageChange = async (newPage: number) => {
    setLogsPage(newPage);
    if (userLogs) {
      await fetchUserLogs(userLogs.user.id, newPage, logsPerPage);
    }
  };
  const fetchUserTransactions = async (
    userId: string,
    page: number = 1,
    perPage: number = 20
  ) => {
    try {
      setTransactionsLoading(true);
      const response = await userService.fetchUserTransactions(
        userId,
        page,
        perPage
      );
      setUserTransactions(response.transactions);
      setTransactionsTotal(response.total);
      setTransactionsTotalPages(response.total_pages);
    } catch (err) {
      console.error("Error fetching user transactions:", error);
      setUserTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };
  // const fetchUserTransactions = async (
  //   userId: string,
  //   page: number = 1,
  //   perPage: number = 20
  // ) => {
  //   try {
  //     setTransactionsLoading(true);
  //     const token = localStorage.getItem("adminToken");
  //     if (!token) {
  //       throw new Error("Authentication required");
  //     }

  //     const response = await fetch(
  //       `${ADMIN_API_BASE_URL}/app-users/${userId}/wallet-transactions?page=${page}&per_page=${perPage}`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error("Failed to fetch transactions");
  //     }

  //     const data = await response.json();
  //     setUserTransactions(data.transactions);
  //     setTransactionsTotal(data.total);
  //     setTransactionsTotalPages(data.total_pages);
  //   } catch (error) {
  //     console.error("Error fetching user transactions:", error);
  //     setUserTransactions([]);
  //   } finally {
  //     setTransactionsLoading(false);
  //   }
  // };

  const handleViewTransactionHistory = async (user: AppUser) => {
    setSelectedUser(user);
    setTransactionsPage(1);
    await fetchUserTransactions(user.id, 1, transactionsPerPage);
    setShowTransactionHistoryModal(true);
  };

  const handleTransactionsPageChange = async (newPage: number) => {
    setTransactionsPage(newPage);
    if (selectedUser) {
      await fetchUserTransactions(
        selectedUser.id,
        newPage,
        transactionsPerPage
      );
    }
  };
  console.log(selectedUser, "selectedUserselectedUser");
  useEffect(() => {
    if (!selectedUser?.profile_picture) {
      setProfileImage(null);
      console.log("Hiiiiiii");
      return;
    }

    const fetchImage = async () => {
      try {
        const url = await getImageUrl(selectedUser.profile_picture, null, "");
        console.log(
          url,
          "============url==========",
          selectedUser?.profile_picture
        );
        setProfileImage(url);
      } catch (err) {
        console.error("Error fetching image URL:", err);
        setProfileImage(null);
      }
    };

    fetchImage();
  }, [selectedUser]);
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };
  console.log(profileImage, "=========profileImage");
  return (
    // Main Header
    <div>
      <MainHeader onBackToDashboard={onBackToDashboard} />

      {/* page header */}

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between mb-6 ">
            <div>
              <motion.button
                onClick={onBackToDashboard}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors duration-200"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Dashboard
              </motion.button>

              <motion.h1
                className="text-3xl font-bold text-gray-900"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                App User Management
              </motion.h1>

              <motion.p
                className="mt-2 text-sm text-gray-600"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                Manage user app, account, and settings
              </motion.p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <svg
                  className="w-5 h-5 text-red-400 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Users
                        {/* Total {filterRole == "all" ? "Users" : filterRole} */}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {userStats?.total_users || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Active Users
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {userStats?.active_users || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Suspended Users
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {userStats?.suspended_users || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Search by name, email, mobile, or User ID"
                  value={search}
                  onChange={handleSearchChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={handleStatusChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provider
                </label>
                <select
                  value={provider}
                  onChange={handleProviderChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {providerOption.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  KYC Status
                </label>
                <select
                  value={kycFilter}
                  onChange={handleKycChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {kycOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Date Range
                </label>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    value={dateRangeFilter.start}
                    onChange={(e) =>
                      handleDateRangeChange("start", e.target.value)
                    }
                    className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="date"
                    value={dateRangeFilter.end}
                    onChange={(e) =>
                      handleDateRangeChange("end", e.target.value)
                    }
                    className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleClearFilters}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 text-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading users...</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50 text-gray-700 text-left">
                        <th className="py-3 px-4 font-medium">Name</th>
                        <th className="py-3 px-4 font-medium">Email</th>
                        <th className="py-3 px-4 font-medium">Mobile</th>
                        <th className="py-3 px-4 font-medium">Provider</th>
                        <th className="py-3 px-4 font-medium">
                          Email Verified
                        </th>
                        <th className="py-3 px-4 font-medium">
                          Phone Verified
                        </th>
                        <th className="py-3 px-4 font-medium">KYC Status</th>
                        <th className="py-3 px-4 font-medium">
                          Wallet Balance
                        </th>
                        <th className="py-3 px-4 font-medium">
                          Account Status
                        </th>
                        <th className="py-3 px-4 font-medium">Last Login</th>
                        <th className="py-3 px-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td
                            colSpan={10}
                            className="text-center py-8 text-gray-500"
                          >
                            No users found.
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr
                            key={user.id}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="py-3 px-4 font-medium">
                              {user.full_name}
                            </td>
                            <td className="py-3 px-4">{user.email}</td>
                            <td className="py-3 px-4">
                              {user.mobile || "N/A"}
                            </td>
                            <td className="py-3 px-4">
                              {user.auth_provider || "email"}
                            </td>
                            <td className="py-3 px-4">
                              {user.email_verified ? "✅" : "❌"}
                            </td>
                            <td className="py-3 px-4">
                              {user.mobile_verified ? "✅" : "❌"}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getKycStatusColor(
                                  user.kyc_status || "not_submitted"
                                )}`}
                              >
                                {user.kyc_status || "not_submitted"}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {formatCurrency(user.wallet_balance || 0)}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getAccountStatusColor(
                                  user.account_status
                                )}`}
                              >
                                {user.account_status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm">
                              {user.last_login || "Never"}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleViewProfile(user)}
                                  className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm"
                                >
                                  View Profile
                                </button>
                                <button
                                  onClick={() => handleToggleStatus(user.id)}
                                  className={
                                    user.account_status === "active"
                                      ? "bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-sm"
                                      : "bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 text-sm"
                                  }
                                >
                                  {user.account_status === "active"
                                    ? "Suspend"
                                    : "Activate"}
                                </button>
                                <button
                                  onClick={() => openEditModal(user)}
                                  className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 text-sm"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleViewLogs(user)}
                                  className="bg-purple-500 text-white px-3 py-1 rounded hover:bg-purple-600 text-sm flex items-center"
                                  title="View Logs"
                                >
                                  <FiActivity className="w-3 h-3 mr-1" />
                                  Logs
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing {(currentPage - 1) * perPage + 1} to{" "}
                        {Math.min(currentPage * perPage, totalUsers)} of{" "}
                        {totalUsers} users
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                        >
                          Previous
                        </button>
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            const page =
                              Math.max(
                                1,
                                Math.min(totalPages - 4, currentPage - 2)
                              ) + i;
                            return (
                              <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`px-3 py-1 rounded ${
                                  page === currentPage
                                    ? "bg-blue-600 text-white font-medium"
                                    : "border hover:bg-gray-100"
                                }`}
                              >
                                {page}
                              </button>
                            );
                          }
                        )}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* User Profile Modal */}
          {showProfileModal && selectedUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">
                      User Profile: {selectedUser.full_name}
                    </h2>
                    <button
                      onClick={closeProfileModal}
                      className="text-gray-500 hover:text-gray-700 text-xl"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Profile Picture */}
                    {selectedUser.profile_picture && (
                      <div className="lg:col-span-2 flex justify-center mb-4">
                        <img
                          src={profileImage ?? ""}
                          alt={selectedUser.full_name}
                          className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                        />
                      </div>
                    )}

                    {/* Basic Information */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold mb-3">
                        Basic Information
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>Full Name:</strong> {selectedUser.full_name}
                        </p>
                        <p>
                          <strong>Email:</strong> {selectedUser.email}
                        </p>
                        <p>
                          <strong>Mobile:</strong>{" "}
                          {selectedUser.mobile || "N/A"}
                        </p>
                        <p>
                          <strong>Date of Birth:</strong>{" "}
                          {selectedUser.date_of_birth || "N/A"}
                        </p>
                        <p>
                          <strong>Gender:</strong>{" "}
                          {selectedUser.gender || "N/A"}
                        </p>
                        <p>
                          <strong>Father's Name:</strong>{" "}
                          {selectedUser.father_name || "N/A"}
                        </p>
                        <p>
                          <strong>User ID:</strong> {selectedUser.id}
                        </p>
                        <p>
                          <strong>Created:</strong>{" "}
                          {new Date(
                            selectedUser.created_at
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {/* Account Status */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold mb-3">
                        Account Status
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>Status:</strong>
                          <span
                            className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getAccountStatusColor(
                              selectedUser.account_status
                            )}`}
                          >
                            {selectedUser.account_status}
                          </span>
                        </p>
                        <p>
                          <strong>Last Login:</strong>{" "}
                          {selectedUser.last_login || "Never"}
                        </p>
                        <p>
                          <strong>KYC Status:</strong>
                          <span
                            className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getKycStatusColor(
                              selectedUser.kyc_status || "not_submitted"
                            )}`}
                          >
                            {selectedUser.kyc_status || "not_submitted"}
                          </span>
                          <button
                            className="ml-2 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                            onClick={() => {
                              setShowKYCHistoryModal(true);
                            }}
                          >
                            View KYC History
                          </button>
                        </p>
                      </div>
                    </div>

                    {/* Wallet Information */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold mb-3">
                        Wallet Information
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>Total Balance:</strong>{" "}
                          {formatCurrency(selectedUser.wallet_balance || 0)}
                        </p>
                        <p>
                          <strong>Deposit Balance:</strong>{" "}
                          {formatCurrency(selectedUser.deposit_balance || 0)}
                        </p>
                        <p>
                          <strong>Winnings Balance:</strong>{" "}
                          {formatCurrency(selectedUser.winnings_balance || 0)}
                        </p>
                        <p>
                          <strong>Withdrawable Balance:</strong>{" "}
                          {formatCurrency(
                            selectedUser.withdrawable_balance || 0
                          )}
                        </p>
                        <button
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          onClick={() =>
                            handleViewTransactionHistory(selectedUser)
                          }
                        >
                          View Transaction History →
                        </button>
                      </div>
                    </div>

                    {/* Challenge Activity */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold mb-3">
                        Challenge Activity
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>Challenges Joined:</strong>{" "}
                          {selectedUser.total_challenges_joined || 0}
                        </p>
                        <p>
                          <strong>Challenges Won:</strong>{" "}
                          {selectedUser.total_challenges_won || 0}
                        </p>
                        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                          View User's Challenges →
                        </button>
                      </div>
                    </div>

                    {/* KYC Documents */}
                    {selectedUser.kyc_documents &&
                      selectedUser.kyc_documents.length > 0 && (
                        <div className="lg:col-span-2 bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-semibold mb-3">
                            KYC Documents
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {selectedUser.kyc_documents.map((doc) => (
                              <div key={doc.id} className="border rounded p-3">
                                <p>
                                  <strong>Type:</strong> {doc.type}
                                </p>
                                <p>
                                  <strong>Status:</strong>
                                  <span
                                    className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getKycStatusColor(
                                      doc.status
                                    )}`}
                                  >
                                    {doc.status}
                                  </span>
                                </p>
                                <p>
                                  <strong>Submitted:</strong>{" "}
                                  {new Date(
                                    doc.submitted_at
                                  ).toLocaleDateString()}
                                </p>
                                {doc.url && (
                                  <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    View Document →
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Authentication & Verification */}
                    <div className="bg-gray-50 p-4 rounded-lg mt-4">
                      <h3 className="text-lg font-semibold mb-3">
                        Authentication & Verification
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>Provider:</strong>{" "}
                          {selectedUser.auth_provider || "email"}
                        </p>
                        <p>
                          <strong>Email Verified:</strong>{" "}
                          {selectedUser.email_verified ? "Yes" : "No"}
                        </p>
                        <p>
                          <strong>Phone Verified:</strong>{" "}
                          {selectedUser.mobile_verified ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>

                    {/* Personalization Preferences */}
                    {selectedUser.preferences && (
                      <div className="bg-gray-50 p-4 rounded-lg mt-4">
                        <h3 className="text-lg font-semibold mb-3">
                          Personalization Preferences
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p>
                            <strong>Experience Level:</strong>{" "}
                            {selectedUser.preferences.experience_level || "N/A"}
                          </p>
                          <p>
                            <strong>Trading Frequency:</strong>{" "}
                            {selectedUser.preferences.trading_frequency ||
                              "N/A"}
                          </p>
                          <p>
                            <strong>Trading Playgrounds:</strong>{" "}
                            {selectedUser.preferences.trading_playgrounds?.join(
                              ", "
                            ) || "N/A"}
                          </p>
                          <p>
                            <strong>Risk Appetite:</strong>{" "}
                            {selectedUser.preferences.risk_appetite || "N/A"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                    <button
                      onClick={() => openResetPasswordModal(selectedUser.id)}
                      className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                    >
                      Reset Password
                    </button>
                    <button
                      onClick={() => handleToggleStatus(selectedUser.id)}
                      className={
                        selectedUser.account_status === "active"
                          ? "bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                          : "bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                      }
                    >
                      {selectedUser.account_status === "active"
                        ? "Suspend Account"
                        : "Activate Account"}
                    </button>
                    <button
                      onClick={() => openEditModal(selectedUser)}
                      className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                    >
                      Edit User
                    </button>
                    <button
                      onClick={closeProfileModal}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reset Password Modal */}
          {showResetPasswordModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Reset User Password
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={resetPasswordData.newPassword}
                      onChange={(e) =>
                        setResetPasswordData((prev) => ({
                          ...prev,
                          newPassword: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowResetPasswordModal(false)}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                      disabled={resetPasswordLoading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResetPassword}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                      disabled={resetPasswordLoading}
                    >
                      {resetPasswordLoading ? "Resetting..." : "Reset Password"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit User Modal */}
          {showEditModal && editUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <h3 className="text-lg font-semibold mb-4">Edit User</h3>
                {editError && (
                  <div className="bg-red-100 text-red-700 p-2 rounded mb-2">
                    {editError}
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={editForm.full_name}
                      onChange={handleEditFormChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={editForm.email}
                      onChange={handleEditFormChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile
                    </label>
                    <input
                      type="text"
                      name="mobile"
                      value={editForm.mobile}
                      onChange={handleEditFormChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={editForm.date_of_birth}
                      onChange={handleEditFormChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={editForm.gender}
                      onChange={handleEditFormChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Father's Name
                    </label>
                    <input
                      type="text"
                      name="father_name"
                      value={editForm.father_name}
                      onChange={handleEditFormChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      KYC Status
                    </label>
                    <select
                      name="kyc_status"
                      value={editForm.kyc_status}
                      onChange={handleEditFormChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="verified">Verified</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                      <option value="not_submitted">Not Submitted</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Status
                    </label>
                    <select
                      name="account_status"
                      value={editForm.account_status}
                      onChange={handleEditFormChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={closeEditModal}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                      disabled={editLoading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                      disabled={editLoading}
                    >
                      {editLoading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* User Logs Modal */}
          {logsDialogOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">
                      User Logs: {selectedUser?.full_name}
                    </h2>
                    <button
                      onClick={() => setLogsDialogOpen(false)}
                      className="text-gray-500 hover:text-gray-700 text-xl"
                    >
                      ✕
                    </button>
                  </div>

                  {logsLoading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-600">Loading logs...</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr className="bg-gray-50 text-gray-700 text-left">
                              <th className="py-3 px-4 font-medium">ID</th>
                              <th className="py-3 px-4 font-medium">
                                Endpoint
                              </th>
                              <th className="py-3 px-4 font-medium">Method</th>
                              <th className="py-3 px-4 font-medium">
                                Request Data
                              </th>
                              <th className="py-3 px-4 font-medium">
                                Response Status
                              </th>
                              <th className="py-3 px-4 font-medium">
                                Response Data
                              </th>
                              <th className="py-3 px-4 font-medium">
                                IP Address
                              </th>
                              <th className="py-3 px-4 font-medium">
                                User Agent
                              </th>
                              <th className="py-3 px-4 font-medium">
                                Timestamp
                              </th>
                              <th className="py-3 px-4 font-medium">
                                Execution Time (ms)
                              </th>
                              <th className="py-3 px-4 font-medium">Success</th>
                              <th className="py-3 px-4 font-medium">
                                Error Message
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {userLogs?.logs?.length > 0 ? (
                              userLogs?.logs.map((log: any) => (
                                <tr
                                  key={log.id}
                                  className="border-b hover:bg-gray-50"
                                >
                                  <td className="py-3 px-4 text-sm">
                                    {log.id}
                                  </td>
                                  <td className="py-3 px-4">{log.endpoint}</td>
                                  <td className="py-3 px-4">{log.method}</td>
                                  <td className="py-3 px-4">
                                    {JSON.stringify(log.request_data)}
                                  </td>
                                  <td className="py-3 px-4">
                                    {log.response_status}
                                  </td>
                                  <td className="py-3 px-4">
                                    {JSON.stringify(log.response_data)}
                                  </td>
                                  <td className="py-3 px-4">
                                    {log.ip_address || "N/A"}
                                  </td>
                                  <td className="py-3 px-4">
                                    {log.user_agent || "N/A"}
                                  </td>
                                  <td className="py-3 px-4">
                                    {formatTimestamp(log.timestamp)}
                                  </td>
                                  <td className="py-3 px-4">
                                    {log.execution_time_ms || "N/A"}
                                  </td>
                                  <td className="py-3 px-4">
                                    {log.success ? "✅" : "❌"}
                                  </td>
                                  <td className="py-3 px-4">
                                    {log.error_message || "N/A"}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                {" "}
                                <td
                                  colSpan={12}
                                  className="py-6 text-center text-gray-500"
                                >
                                  No Logs Found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {userLogs?.pagination &&
                        userLogs.pagination.total_pages > 1 && (
                          <div className="px-6 py-4 border-t bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-700">
                                Showing {(logsPage - 1) * logsPerPage + 1} to{" "}
                                {Math.min(
                                  logsPage * logsPerPage,
                                  userLogs.pagination.total
                                )}{" "}
                                of {userLogs.pagination.total} logs
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() =>
                                    handleLogsPageChange(logsPage - 1)
                                  }
                                  disabled={logsPage === 1}
                                  className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                >
                                  Previous
                                </button>
                                {Array.from(
                                  {
                                    length: Math.min(
                                      5,
                                      userLogs.pagination.total_pages
                                    ),
                                  },
                                  (_, i) => {
                                    const page =
                                      Math.max(
                                        1,
                                        Math.min(
                                          userLogs.pagination.total_pages - 4,
                                          logsPage - 2
                                        )
                                      ) + i;
                                    return (
                                      <button
                                        key={page}
                                        onClick={() =>
                                          handleLogsPageChange(page)
                                        }
                                        className={`px-3 py-1 rounded ${
                                          page === logsPage
                                            ? "bg-blue-600 text-white font-medium"
                                            : "border hover:bg-gray-100"
                                        }`}
                                      >
                                        {page}
                                      </button>
                                    );
                                  }
                                )}
                                <button
                                  onClick={() =>
                                    handleLogsPageChange(logsPage + 1)
                                  }
                                  disabled={
                                    logsPage === userLogs.pagination.total_pages
                                  }
                                  className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* KYC History Modal */}
          {showKYCHistoryModal && selectedUser && (
            <KYCHistoryModal
              userId={selectedUser.id}
              userName={selectedUser.full_name}
              onClose={() => setShowKYCHistoryModal(false)}
            />
          )}

          {/* Transaction History Modal */}
          {showTransactionHistoryModal && selectedUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold">
                      Transaction History - {selectedUser.full_name}
                    </h3>
                    <button
                      onClick={() => setShowTransactionHistoryModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ✕
                    </button>
                  </div>

                  {transactionsLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-600">
                        Loading transactions...
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr className="bg-gray-50 text-gray-700 text-left">
                              <th className="py-3 px-4 font-medium">Type</th>
                              <th className="py-3 px-4 font-medium">Amount</th>
                              <th className="py-3 px-4 font-medium">Status</th>
                              <th className="py-3 px-4 font-medium">
                                Description
                              </th>
                              <th className="py-3 px-4 font-medium">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userTransactions.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="text-center py-8 text-gray-500"
                                >
                                  No transactions found.
                                </td>
                              </tr>
                            ) : (
                              userTransactions.map((transaction) => (
                                <tr
                                  key={transaction.id}
                                  className="border-b hover:bg-gray-50"
                                >
                                  <td className="py-3 px-4">
                                    <span
                                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        transaction.transaction_type ===
                                        "deposit"
                                          ? "bg-green-100 text-green-800"
                                          : transaction.transaction_type ===
                                            "withdrawal"
                                          ? "bg-red-100 text-red-800"
                                          : transaction.transaction_type ===
                                            "winnings"
                                          ? "bg-blue-100 text-blue-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {transaction.transaction_type}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 font-medium">
                                    {formatCurrency(transaction.amount || 0)}
                                  </td>
                                  <td className="py-3 px-4">
                                    <span
                                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        transaction.status === "completed"
                                          ? "bg-green-100 text-green-800"
                                          : transaction.status === "pending"
                                          ? "bg-yellow-100 text-yellow-800"
                                          : transaction.status === "failed"
                                          ? "bg-red-100 text-red-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {transaction.status}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-sm">
                                    {transaction.description || "N/A"}
                                  </td>
                                  <td className="py-3 px-4 text-sm">
                                    {new Date(
                                      transaction.created_at
                                    ).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {transactionsTotalPages > 1 && (
                        <div className="mt-6 flex items-center justify-between">
                          <div className="text-sm text-gray-700">
                            Showing{" "}
                            {(transactionsPage - 1) * transactionsPerPage + 1}{" "}
                            to{" "}
                            {Math.min(
                              transactionsPage * transactionsPerPage,
                              transactionsTotal
                            )}{" "}
                            of {transactionsTotal} transactions
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() =>
                                handleTransactionsPageChange(
                                  transactionsPage - 1
                                )
                              }
                              disabled={transactionsPage === 1}
                              className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                            >
                              Previous
                            </button>
                            {Array.from(
                              { length: Math.min(5, transactionsTotalPages) },
                              (_, i) => {
                                const page =
                                  Math.max(
                                    1,
                                    Math.min(
                                      transactionsTotalPages - 4,
                                      transactionsPage - 2
                                    )
                                  ) + i;
                                return (
                                  <button
                                    key={page}
                                    onClick={() =>
                                      handleTransactionsPageChange(page)
                                    }
                                    className={`px-3 py-1 rounded ${
                                      page === transactionsPage
                                        ? "bg-blue-600 text-white font-medium"
                                        : "border hover:bg-gray-100"
                                    }`}
                                  >
                                    {page}
                                  </button>
                                );
                              }
                            )}
                            <button
                              onClick={() =>
                                handleTransactionsPageChange(
                                  transactionsPage + 1
                                )
                              }
                              disabled={
                                transactionsPage === transactionsTotalPages
                              }
                              className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
